import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a cryptographically secure alphanumeric ID with very low collision probability.
 * Uses a combination of timestamp, crypto random values, and base conversion for uniqueness.
 *
 * @param length - The desired length of the generated ID (default: 16)
 * @returns A string containing only alphanumeric characters (a-z, A-Z, 0-9)
 */
export function generateAlphanumericId(length: number = 16): string {
  // Use crypto.getRandomValues for cryptographically secure randomness
  const array = new Uint8Array(Math.ceil(length * 0.75)); // Generate more bytes than needed for better distribution
  crypto.getRandomValues(array);

  // Convert to base62 (alphanumeric: a-z, A-Z, 0-9)
  const chars =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';

  // Add timestamp component for temporal uniqueness (first 8 chars)
  const timestamp = Date.now().toString(36).toLowerCase();
  result += timestamp.slice(-8).padStart(8, '0');

  // Add random component for the remaining length
  for (let i = 8; i < length; i++) {
    const randomIndex = array[i % array.length] % chars.length;
    result += chars[randomIndex];
  }

  return result.slice(0, length);
}
